import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { create } from "@/lib/store";
import { MetricType } from "@/shared/types";
import { hashStorage } from "@/shared/utils/hashStorage";

import { DEFAULT_COLUMNS } from "../constants/defaults";
import { CurveType } from "../constants/maps";
import { ComparisonMode } from "../types/comparisonMode";

const STORE_NAME = "Investment Levels V2";

export enum SaturationCurvesView {
  CURVES = "Curves",
  TABLE = "Table",
}

export enum InvestmentLevelsGridFilters {
  CURVE_SHAPE = "Curve Shape",
  SPEND = "Current Spend",
  REVENUE = "Current Revenue",
  WEEKS_IN_MARKET = "Weeks in Market",
  MARGINAL_ROAS = "Marginal ROAS",
  YIELD = "Yield",
  STATUS = "Status",
  MAX_EFFICIENCY = "Max Efficiency",
  DIFFERENCE_TO_MAX = "Difference to Max",
  LOWER_BOUND = "Lower Threshold",
  UPPER_BOUND = "Upper Threshold",
}

interface InvestmentLevelsState {
  filters: {
    targetNodeUUID: string | null;
    comparisonMode: ComparisonMode;
    channels: string[];
    geography: string;
  };
  curves: {
    activeCurveIds: string[];
    curveType: CurveType;
  };
  grid: {
    activeColumns: string[];
    metricType: MetricType;
  };
  saturationCurvesView: SaturationCurvesView;
}

interface InvestmentLevelsActions {
  setTargetNodeUUID: (nodeUUID: string | null) => void;
  setComparisonMode: (mode: ComparisonMode) => void;
  setChannels: (channels: string[]) => void;
  setGeography: (geography: string) => void;
  setActiveCurveIds: (curveIds: string[]) => void;
  setCurveType: (type: CurveType) => void;
  setActiveColumns: (columns: string[]) => void;
  setMetricType: (metricType: MetricType) => void;
  setSaturationCurvesView: (view: SaturationCurvesView) => void;
}

type InvestmentLevelsStore = InvestmentLevelsState & InvestmentLevelsActions;

const defaultState: InvestmentLevelsState = {
  filters: {
    targetNodeUUID: null,
    comparisonMode: ComparisonMode.CHANNEL,
    channels: [],
    geography: "",
  },
  curves: {
    activeCurveIds: [],
    curveType: CurveType.YIELD,
  },
  grid: {
    activeColumns: DEFAULT_COLUMNS,
    metricType: MetricType.MARKETING_ROI,
  },
  saturationCurvesView: SaturationCurvesView.CURVES,
};

export const useInvestmentLevelsStore = create<InvestmentLevelsStore>()(
  devtools(
    persist(
      immer((set) => ({
        ...defaultState,
        setTargetNodeUUID: (nodeUUID: string | null) =>
          set((state) => {
            state.filters.targetNodeUUID = nodeUUID;
          }),
        setComparisonMode: (mode: ComparisonMode) =>
          set((state) => {
            state.filters.comparisonMode = mode;
          }),
        setChannels: (channels: string[]) =>
          set((state) => {
            state.filters.channels = channels;
          }),
        setGeography: (geography: string) =>
          set((state) => {
            state.filters.geography = geography;
          }),
        setActiveCurveIds: (curveIds: string[]) =>
          set((state) => {
            state.curves.activeCurveIds = curveIds;
          }),
        setCurveType: (type: CurveType) =>
          set((state) => {
            state.curves.curveType = type;
          }),
        setActiveColumns: (columns) =>
          set((state) => {
            state.grid.activeColumns = columns;
          }),
        setMetricType: (metricType: MetricType) =>
          set((state) => {
            state.grid.metricType = metricType;
          }),
        setSaturationCurvesView: (view: SaturationCurvesView) => {
          set((state) => {
            state.saturationCurvesView = view;
          });
        },
      })),
      {
        name: STORE_NAME,
        storage: createJSONStorage(() => hashStorage),
      }
    ),
    { name: STORE_NAME }
  )
);
